import { Controller, useFormContext } from "react-hook-form";

import { InformationIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import { FeatureDetail } from "@/types/features";
import {
  AddGroupFormData,
  EditGroupFormData,
  UserGroupPermissionPayload
} from "@/types/groups";

import { getPermissionOptions, PermissionInfoTooltipContent } from "../data";

export type PermissionTableProps = {
  isSubmitting?: boolean;
  permissions: UserGroupPermissionPayload[] | FeatureDetail[];
};

const PermissionTable = ({
  isSubmitting,
  permissions
}: PermissionTableProps) => {
  const { control, setValue } = useFormContext<
    AddGroupFormData | EditGroupFormData
  >();
  return (
    <div className="flex flex-1 justify-between gap-4 mt-1 mb-4">
      <ScrollArea className="flex-1 h-auto">
        <div className="rounded-lg border">
          <Table className="text-xs">
            <TableHeader className="flex flex-col flex-1 z-10">
              <TableRow className="flex items-center bg-white-smoke">
                <TableHead className="flex items-center flex-1 px-3 py-2">
                  <strong>Feature/ Module</strong>
                </TableHead>
                <TableHead className="flex gap-2 items-center basis-[189px] px-3 py-2">
                  <strong>Permission</strong>
                  <Tooltip
                    content={<PermissionInfoTooltipContent />}
                    position="bottom-center"
                  >
                    <button
                      type="button"
                      aria-label="Permission Info"
                      className="bg-transparent border-0 p-0"
                    >
                      <InformationIcon />
                    </button>
                  </Tooltip>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="flex flex-col flex-1">
              {permissions?.map((row, index) => (
                <TableRow
                  className="flex flex-1 last:border-b-0"
                  key={`row-${index}-${row.name}`}
                >
                  <TableCell className="flex-1 flex items-center px-3 py-2">
                    <strong>{row.name}</strong>
                  </TableCell>
                  <TableCell className="relative flex-0 basis-[189px] text-center px-3 py-2">
                    <Controller
                      name={`group_permission.${index}.selectedPermission.value`}
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange } }) => {
                        const selectedOption =
                          "selectedPermission" in row
                            ? row.selectedPermission
                            : null;
                        const options = getPermissionOptions(row.name);
                        return (
                          <Dropdown
                            value={selectedOption}
                            name={`group_permission.${index}.selectedPermission.value`}
                            className="mt-1"
                            labelBorder="Select"
                            placeholder="Select"
                            data={options}
                            disabled={isSubmitting}
                            onSelect={(data) => {
                              onChange(data?.value);
                              // Update the form value for selectedPermission
                              setValue(
                                `group_permission.${index}.selectedPermission.label`,
                                data?.label ?? ""
                              );
                              setValue(
                                `group_permission.${index}.selectedPermission.value`,
                                data?.value ?? ""
                              );
                            }}
                          />
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  );
};

export default PermissionTable;
